export const AuthModes = {
  REQUIRED: 'required',
  BOOTSTRAP: 'bootstrap',
  PUBLIC: 'public'
} as const

export type AuthMode = (typeof AuthModes)[keyof typeof AuthModes]

type LegacyComponentConfig = {
  isPublic?: boolean
  auth?: {
    mode?: AuthMode
    requiresDigitalProductId?: boolean
  }
}

type AuthPolicy = {
  mode: AuthMode
  requiresDigitalProductId: boolean
}

const authModeByPath = (pathname?: string): AuthMode | null => {
  if (!pathname) return null
  if (pathname.startsWith('/auth/')) return AuthModes.BOOTSTRAP
  return null
}

const BOOTSTRAP_PATH_PREFIXES = ['/auth/', '/sign-out']
const PUBLIC_PATH_PREFIXES = ['/sem-evento']

const inferRequiresDigitalProductId = (pathname?: string) => {
  if (!pathname) return true

  if (PUBLIC_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))) {
    return false
  }

  if (BOOTSTRAP_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))) {
    return false
  }

  return true
}

export const resolveAuthPolicy = (
  component: LegacyComponentConfig,
  pathname?: string
): AuthPolicy => {
  const fromComponent = component?.auth
  const legacyMode = component?.isPublic ? AuthModes.PUBLIC : null
  const mode =
    fromComponent?.mode || authModeByPath(pathname) || legacyMode || AuthModes.REQUIRED

  const requiresDigitalProductId =
    typeof fromComponent?.requiresDigitalProductId === 'boolean'
      ? fromComponent.requiresDigitalProductId
      : mode === AuthModes.REQUIRED && inferRequiresDigitalProductId(pathname)

  return { mode, requiresDigitalProductId }
}

type RouteMatchStaticData = {
  staticData?: LegacyComponentConfig
}

export const resolveAuthPolicyFromMatches = (
  matches: RouteMatchStaticData[],
  pathname?: string
): AuthPolicy => {
  const mergedStaticData: LegacyComponentConfig = {}

  for (const match of matches) {
    Object.assign(mergedStaticData, match.staticData)

    if (match.staticData?.auth) {
      mergedStaticData.auth = {
        ...mergedStaticData.auth,
        ...match.staticData.auth
      }
    }
  }

  const leaf = matches[matches.length - 1]?.staticData

  if (leaf?.auth?.mode === AuthModes.BOOTSTRAP) {
    return {
      mode: AuthModes.BOOTSTRAP,
      requiresDigitalProductId: false
    }
  }

  if (leaf?.auth?.mode === AuthModes.PUBLIC) {
    return {
      mode: AuthModes.PUBLIC,
      requiresDigitalProductId: false
    }
  }

  if (leaf?.isPublic) {
    return {
      mode: AuthModes.PUBLIC,
      requiresDigitalProductId: false
    }
  }

  return resolveAuthPolicy(mergedStaticData, pathname)
}
